import { createActionGroup, emptyProps, props } from '@ngrx/store';
import { IAbacusConnectBody } from '../models/integration/abacus-connect.model';
import { ITopalConnectBody } from '../models/integration/topal-connect.model';
import { IAccountoConnectBody } from '../models/integration/accounto-connect.model';
import { IProffixConnectBody } from '../models/integration/proffix-connect.model';
import { ICompanyMappingSuggestion } from '../models/integration/company-mapping-suggestion.model';
import {
    IAumicoCompanyWithMapping,
    IConnectionMapping,
    IUpsertConnectionMappingBody,
} from '../models/integration/connection-mapping.model';
import { IAbacusImportBody } from '../models/integration/abacus-import.model';
import { ITopalImportBody } from '../models/integration/topal-import.model';
import { IAccountoImportBody } from '../models/integration/accounto-import.model';
import { IProffixImportBody } from '../models/integration/proffix-import.model';
import {
    ID365BcImportBody,
    IFiscalYear,
} from '../models/integration/d365bc-import.model';
import { IJournalImportResult } from '../models/integration/journal-import.model';
import { IIntegrationConnection } from '../models/integration/integration-connection.model';
import { IntegrationVendor } from '../models/integration/integration-vendor.enum';
import {
    SelectedClosingFolder,
    SharepointConnection,
} from '../components/sharepoint-folders/sharepoint-folders.types';
import { PickedSharepointFolder } from '../services/sharepoint-file-picker.service';

export const integrationActions = createActionGroup({
    source: 'Integration',
    events: {
        // ── Phase 1: Connections list ──
        'Load Connections': emptyProps(),
        'Load Connections Success': props<{ connections: IIntegrationConnection[] }>(),
        'Load Connections Failure': props<{ error: string }>(),

        // ── Phase 1: Single-shot consent (BE handles consent → test → create atomically) ──
        // FE submits the form upfront; BE returns the OAuth URL; popup posts back success/error.
        'Initiate Consent': props<{
            vendor: IntegrationVendor;
            environmentName: string;
            displayName?: string;
        }>(),
        'Consent Popup Opened': emptyProps(),
        'Consent Completed': emptyProps(),
        'Consent Cancelled': emptyProps(),
        'Consent Failed': props<{ error: string }>(),

        'Cancel Setup': emptyProps(),

        // ── SharePoint ORG connect (REV-21860) ──────────────────────────────
        //
        // The Org Settings integration card's connect flow. Distinct from
        // `Sharepoint Connect Initiate` above, which is CLOSING-scoped (it polls
        // `/projects/:id/sharepoint-connection` and reloads that closing's folders).
        // This one is the plain per-org connect the card needs: the BE mints the
        // connection from the caller's organization, so there is no projectId.
        //
        // It deliberately drives the SAME generic `pendingSetup` slice the accounting
        // vendors use, so the reducer's vendor-agnostic post-connect detection in
        // `loadConnectionsSuccess` flips the step to `created` and emits
        // `connectionJustCreated` for SharePoint exactly as it does for D365 BC.
        // Consequently the terminal actions are the GENERIC ones
        // (`consentPopupOpened` / `consentFailed` / `cancelSetup`), not the
        // `sharepoint*` ones — those belong to the closing-scoped sub-slice.
        'Sharepoint Org Connect Initiate': props<{ displayName?: string }>(),


        // ── Abacus connect (no popup, single HTTP roundtrip) ────────────
        //
        // The user enters serverUrl + clientId + clientSecret + displayName in
        // a form. We POST once; BE runs OIDC discovery + token + smoke test +
        // persist atomically. On success the new connection is in the BE; we
        // dispatch loadConnections so the reducer picks it up and flips
        // pendingSetup → `created`, which auto opens the manage modal via the
        // same `connectionJustCreated` path D365 BC uses.
        //
        // Security: `clientSecret` is in this action's payload. The DevTools
        // instrumentation runs in production too (`logOnly` still records
        // payloads), so the reports app passes `redactConnectSecretsForDevtools`
        // as `actionSanitizer` — it blanks the secret in the action history
        // (REV-22536 review, REV-21119). The action itself reaches the effect
        // unchanged and goes only over HTTP to the BE, which encrypts it at rest.
        // Do NOT add a generic action logger that ships actions to a remote.
        'Initiate Abacus Connect': props<{ body: IAbacusConnectBody }>(),
        'Abacus Connect Success': props<{ connection: IIntegrationConnection }>(),
        'Abacus Connect Failure': props<{ error: string }>(),
        // BE returned 409 CONNECTION_ALREADY_EXISTS: this server/tenant is already
        // connected in the org. Not a real failure (no in-modal error view) — we
        // leave the loading state, close the connect modal, reopen the manage
        // (server list) modal and show a toast. See initiateAbacusConnect$ +
        // the overview subscriber.
        'Abacus Connect Already Exists': emptyProps(),

        // ── Topal connect (no popup, single HTTP roundtrip — mirrors Abacus) ──
        //
        // The user enters serverUrl + username + password + displayName in a form.
        // We POST once; the BE mints a password-grant token + smoke test + persist
        // atomically. On success we dispatch loadConnections so the reducer picks up
        // the new connection and flips pendingSetup → `created` (same path as Abacus).
        //
        // Security: `password` is in this action's payload — see the Abacus note above;
        // the same reasoning applies. Do NOT add a remote action logger.
        'Initiate Topal Connect': props<{ body: ITopalConnectBody }>(),
        'Topal Connect Success': props<{ connection: IIntegrationConnection }>(),
        'Topal Connect Failure': props<{ error: string }>(),
        // BE returned 409 CONNECTION_ALREADY_EXISTS: this server is already connected
        // in the org. Not a real failure — mirrors `abacusConnectAlreadyExists`.
        'Topal Connect Already Exists': emptyProps(),

        // ── Accounto connect (no popup, single HTTP roundtrip — mirrors Topal) ──
        //
        // The user pastes one API key + a displayName. We POST once; the BE reads the
        // tenant from the key, checks it against Accounto (tenant, key kind, one account
        // read) and persists atomically. On success we dispatch loadConnections so the
        // reducer flips pendingSetup → `created`.
        //
        // Security: `apiKey` is in this action's payload — see the Abacus note above; the
        // same reasoning applies. Do NOT add a remote action logger.
        'Initiate Accounto Connect': props<{ body: IAccountoConnectBody }>(),
        'Accounto Connect Success': props<{ connection: IIntegrationConnection }>(),
        'Accounto Connect Failure': props<{ error: string }>(),
        // BE returned 409 CONNECTION_ALREADY_EXISTS: this tenant is already connected
        // in the org. Not a real failure — mirrors `topalConnectAlreadyExists`.
        'Accounto Connect Already Exists': emptyProps(),

        // ── Proffix connect (no popup, single HTTP roundtrip — mirrors Topal) ──
        //
        // The user enters serverUrl + REST-API password + username + password +
        // displayName in a form. We POST once; the BE tests the credentials (login →
        // FIB read → logout) and persists atomically. On success we dispatch
        // loadConnections so the reducer flips pendingSetup → `created`.
        //
        // Security: `restApiKey` and `password` are in this action's payload — see the
        // Abacus note above; the same reasoning applies. Do NOT add a remote action logger.
        'Initiate Proffix Connect': props<{ body: IProffixConnectBody }>(),
        'Proffix Connect Success': props<{ connection: IIntegrationConnection }>(),
        'Proffix Connect Failure': props<{ error: string }>(),
        // BE returned 409 CONNECTION_ALREADY_EXISTS: this server is already connected
        // in the org. Not a real failure — mirrors `topalConnectAlreadyExists`.
        'Proffix Connect Already Exists': emptyProps(),

        // ── Phase 1: Existing-connection lifecycle ──
        'Test Existing Connection': props<{ connectionId: string }>(),
        'Test Existing Connection Success': props<{ connectionId: string; lastTestAt: Date }>(),
        'Test Existing Connection Failure': props<{ connectionId: string; error: string }>(),

        Disconnect: props<{ connectionId: string }>(),
        'Disconnect Success': props<{ connectionId: string }>(),
        'Disconnect Failure': props<{ connectionId: string; error: string }>(),

        // ── External companies + mapping ──────────────────────────────
        //
        // FE fetches IExternalCompany[] from the BE and wraps each into an
        // ICompanyMappingSuggestion (with `suggestedAumicoCompany: null`).
        // The manage modal joins these with the tenantDetail Aumico list to
        // produce the per row "BC company → Aumico company" view; the actual
        // name based matching is done server-write via the auto match effect.
        'Load External Companies': props<{ connectionId: string }>(),
        'Load External Companies Success': props<{
            connectionId: string;
            suggestions: ICompanyMappingSuggestion[];
        }>(),
        'Load External Companies Failure': props<{ error: string }>(),

        // Opens the wizard for exactly one external company. The reducer sets
        // `connectionId`, stores the one suggestion, and flips `isWizardOpen = true`.
        // Save in the wizard dispatches `upsertMapping` directly: no decision
        // accumulation, no iteration, no separate finish step.
        'Mapping Start Single Edit': props<{
            connectionId: string;
            vendor: IntegrationVendor;
            suggestion: ICompanyMappingSuggestion;
        }>(),

        // Hide the wizard. Triggered locally by the wizard component on
        // `upsertMappingSuccess` (auto close) and on Cancel / X / Esc.
        'Mapping Cancel': emptyProps(),

        // ── Connection mapping CRUD (ms-companies endpoints) ───────────
        //
        // Mappings are written per Company via `PUT /api/companies/:id/...`.
        // The single edit modal dispatches `upsertMapping` directly; the
        // manage modal dispatches `deleteMapping` for inline Unlink. The
        // auto match effect uses the bulk endpoint instead, not these.

        'Upsert Mapping': props<{
            companyId: string;
            body: IUpsertConnectionMappingBody;
        }>(),
        'Upsert Mapping Success': props<{
            companyId: string;
            mapping: IConnectionMapping;
        }>(),
        'Upsert Mapping Failure': props<{
            companyId: string;
            externalCompanyId: string;
            error: string;
        }>(),

        'Delete Mapping': props<{ companyId: string; vendor: IntegrationVendor }>(),
        'Delete Mapping Success': props<{ companyId: string; vendor: IntegrationVendor }>(),
        'Delete Mapping Failure': props<{ companyId: string; error: string }>(),

        // ── Auto match (one shot after first Connect) ──────────────────
        //
        // The post Connect orchestration: fetch external + Aumico, run the
        // matcher, bulk upsert the result. Exactly one run per new connection
        // by construction (only `triggerMappingAfterCreated$` dispatches the
        // trigger: see effects).

        'Auto Match Trigger': props<{ connectionId: string; vendor: IntegrationVendor }>(),
        'Auto Match Persisted': props<{
            connectionId: string;
            autoMatched: number;
            notMapped: number;
            errors: number;
            /**
             * The per row mappings the bulk upsert successfully wrote, paired
             * with the Aumico Company name (looked up from the matcher input).
             * The reducer uses this to PATCH tenantDetail.aumicoCompanies
             * optimistically: without it the table would sit stale until a
             * separate refresh roundtrip (which may itself race the BE write).
             */
            successfulMappings: Array<{
                aumicoCompanyId: string;
                aumicoCompanyName: string;
                mapping: IConnectionMapping;
            }>;
        }>(),
        'Auto Match Failed': props<{ connectionId: string; error: string }>(),

        // Emitted by the same diffing effect that detects a new connection.
        // The integrations overview reacts by opening the manage modal into
        // the detail view for this connection: so the user lands directly on
        // the auto match result.
        //
        // Carries `vendor` so the overview can pick the right manage modal
        // component. Without it the overview would have to look up the
        // connection by id to know which modal to render.
        'Connection Just Created': props<{ connectionId: string; vendor: IntegrationVendor }>(),

        // ── Tenant detail (D2 use case) ────────────────────────────────
        //
        // `GET /api/companies/by-integration-connection/:cnId` populates the
        // manage modal's detail view: which Aumico Companies are bound to this
        // BC tenant, and what their current mapping looks like.

        'Load Mapped Companies': props<{ connectionId: string }>(),
        'Load Mapped Companies Success': props<{
            connectionId: string;
            companies: IAumicoCompanyWithMapping[];
        }>(),
        'Load Mapped Companies Failure': props<{ error: string }>(),

        // ── D365 BC journal import ─────────────────────────────────────
        //
        // Drives the "Daten synchronisieren" card in the bilance sheet
        // import page. Fiscal years are loaded eagerly when the active
        // company has a D365 BC mapping; the import itself runs on the
        // user clicking Sync on the card.

        'D365BC Fiscal Years Load': props<{
            connectionId: string;
            externalCompanyId: string;
        }>(),
        'D365BC Fiscal Years Load Success': props<{ fiscalYears: IFiscalYear[] }>(),
        'D365BC Fiscal Years Load Failure': props<{ error: string }>(),

        'D365BC Journal Import Run': props<{ body: ID365BcImportBody }>(),
        'D365BC Journal Import Success': props<{ result: IJournalImportResult }>(),
        'D365BC Journal Import Failure': props<{ error: string }>(),

        /** Clears import status + result (modal close, manual override, etc). */
        'D365BC Journal Import Reset': emptyProps(),

        // ── Abacus journal import ────────────────────────────────────────
        //
        // Abacus has no fiscal-year discovery endpoint; the FE pulls dates
        // from the active Annual Report's General Info, so there is no
        // `Abacus Fiscal Years Load` action. Otherwise mirrors D365 BC.
        'Abacus Journal Import Run': props<{ body: IAbacusImportBody }>(),
        'Abacus Journal Import Success': props<{ result: IJournalImportResult }>(),
        'Abacus Journal Import Failure': props<{ error: string }>(),
        'Abacus Journal Import Reset': emptyProps(),

        // ── Topal journal import ─────────────────────────────────────────
        //
        // Like Abacus: no fiscal-year discovery; dates come from the active Annual
        // Report's General Info and the BE resolves the Topal fiscalYearId.
        'Topal Journal Import Run': props<{ body: ITopalImportBody }>(),
        'Topal Journal Import Success': props<{ result: IJournalImportResult }>(),
        // `status` carries the HTTP status so the failure toast can show a specific message —
        // e.g. 404 = the report's period has no matching Topal fiscal year (wrong dates).
        'Topal Journal Import Failure': props<{ error: string; status?: number }>(),
        'Topal Journal Import Reset': emptyProps(),

        // ── Accounto journal import ──────────────────────────────────────
        //
        // Like Abacus: no fiscal-year discovery; dates come from the active Annual
        // Report's General Info and the BE reads the postings of that range directly.
        // `annualReportId` stays in the store (never sent): a late result is applied only to that report.
        'Accounto Journal Import Run': props<{ body: IAccountoImportBody; annualReportId: string }>(),
        'Accounto Journal Import Success': props<{ result: IJournalImportResult }>(),
        // `errorCode` lets the toast name an expired key (`KEY_EXPIRED`) instead of the generic text.
        'Accounto Journal Import Failure': props<{ error: string; status?: number; errorCode?: string }>(),
        'Accounto Journal Import Reset': emptyProps(),

        // ── Proffix journal import ───────────────────────────────────────
        //
        // Like Abacus: no fiscal-year discovery; dates come from the active Annual
        // Report's General Info and the BE filters the journal by them directly.
        // `annualReportId` stays in the store (never sent): a late result is applied only to that report.
        'Proffix Journal Import Run': props<{ body: IProffixImportBody; annualReportId: string }>(),
        'Proffix Journal Import Success': props<{ result: IJournalImportResult }>(),
        'Proffix Journal Import Failure': props<{ error: string; status?: number }>(),
        'Proffix Journal Import Reset': emptyProps(),

        // ── SharePoint / Microsoft 365 document source (REV-21679) ───────────
        //
        // SharePoint is a DOCUMENT SOURCE, not an accounting vendor: it never
        // becomes an `IntegrationVendor` and never carries a ConnectionMapping.
        // Its state lives in a dedicated `sharepoint` sub-slice of IntegrationState.
        //
        // Connect flow mirrors D365 BC's admin-consent popup: the reliable signal is
        // POLLING `Sharepoint Load Connection` while the popup is open (BroadcastChannel
        // 'sharepoint-auth' / message 'sharepoint-auth-success' are cross-origin
        // best-effort fast paths only), and the "created" signal reloads the SharePoint
        // CONNECTION + FOLDERS (not the vendor connections list).
        // See `openSharepointConsentPopupAndWatch$`.
        'Sharepoint Connect Initiate': props<{ projectId: string }>(),
        'Sharepoint Connect Popup Opened': emptyProps(),
        'Sharepoint Connect Completed': props<{ projectId: string }>(),
        // `projectId` (REV-21869): set when the consent popup itself reported the failure, so the connection
        // can be re-read once — the backend may be connected after all (see `recheckSharepointAfterFailedConnect$`).
        'Sharepoint Connect Failed': props<{ error: string; projectId?: string }>(),

        'Sharepoint Load Connection': props<{ projectId: string }>(),
        'Sharepoint Load Connection Success': props<{
            connection: SharepointConnection & { connectionId?: string };
        }>(),
        'Sharepoint Load Connection Failure': props<{ error: string }>(),

        'Sharepoint Load Folders': props<{ projectId: string }>(),
        'Sharepoint Load Folders Success': props<{ folders: SelectedClosingFolder[] }>(),
        'Sharepoint Load Folders Failure': props<{ error: string }>(),

        // `connectionId` is REQUIRED by the BE DTO (`SaveSharepointFoldersDto`) and
        // must travel with the picked folders — the container resolves it from the
        // loaded connection right before it opens the picker.
        'Sharepoint Add Folders': props<{
            projectId: string;
            connectionId: string;
            folders: PickedSharepointFolder[];
        }>(),
        // `projectId` is echoed back so the success effect can re-read the authoritative
        // folder list (REV-21057 BUG 3: the POST only returns the NEWLY created bindings).
        'Sharepoint Add Folders Success': props<{
            projectId: string;
            folders: SelectedClosingFolder[];
        }>(),
        'Sharepoint Add Folders Failure': props<{ error: string }>(),

        // AC B3 — flip 'include subfolders' on an already-saved folder. Optimistic in the
        // reducer so the checkbox does not lag the click; reverted on failure.
        'Sharepoint Update Folder': props<{ projectId: string; folderId: string; includeSubfolders: boolean }>(),
        'Sharepoint Update Folder Success': props<{ folderId: string; includeSubfolders: boolean }>(),
        'Sharepoint Update Folder Failure': props<{ folderId: string; includeSubfolders: boolean; error: string }>(),
        'Sharepoint Remove Folder': props<{ projectId: string; folderId: string }>(),
        'Sharepoint Remove Folder Success': props<{ folderId: string }>(),
        'Sharepoint Remove Folder Failure': props<{ error: string }>(),

        // Picker access token is BE-minted (no MSAL); non-blocking fetch used by
        // the container just before it opens the Microsoft File Picker.
        'Sharepoint Load Picker Token': props<{ connectionId: string }>(),
        // `baseUrl` is the tenant SharePoint Online origin the picker must be opened
        // against (BE best-effort discovery — null when unavailable).
        'Sharepoint Load Picker Token Success': props<{
            token: string;
            baseUrl: string | null;
        }>(),
        'Sharepoint Load Picker Token Failure': props<{ error: string }>(),
    },
});
